import type { Immutable } from 'immer';
import { z } from 'zod';

import { PostInput } from '@tdp/common/shared/tsUtils';
import { ILogger } from '@tdp/common/telemetry';

import { BotFrameworkBot, BotFrameworkBotSchema } from './models';

export interface BotFrameworkBotParseErrorResult {
  type: 'parseError';
  errors: z.ZodError;
}

export interface BotFrameworkBotNetworkErrorResult {
  type: 'networkError';
  errorCode: number;
}

export interface BotFrameworkBotSuccessResult {
  type: 'success';
  bots: BotFrameworkBot[];
}

export type BotFrameworkBotResult =
  | BotFrameworkBotSuccessResult
  | BotFrameworkBotParseErrorResult
  | BotFrameworkBotNetworkErrorResult;
export interface IBotProvider {
  getBots(): Promise<BotFrameworkBotResult>;
  readBotDetails(botId: string): Promise<BotDetailsFetchResult>;
}

export interface IBotWriter {
  createBot(bot: PostInput<BotFrameworkBot, 'botId'>): Promise<BotFrameworkBot>;
  putBot(bot: Immutable<BotFrameworkBot>): Promise<void>;
}

export interface BotReaderDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export interface BotDetailsFetchSuccessResult {
  type: 'success';
  botDetails: BotFrameworkBot;
}

export interface BotDetailsFetchFailureResult {
  type: 'error';
  errorType: 'error' | 'parsingError' | 'networkError';
  errorMessage?: string;
}

export type BotDetailsFetchResult =
  | BotDetailsFetchSuccessResult
  | BotDetailsFetchFailureResult;

export class TdpBotService implements IBotProvider, IBotWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor(deps: BotReaderDeps) {
    this.apiFetch = deps.apiFetch;
    this.logger = deps.logger;
  }
  async createBot(
    bot: PostInput<BotFrameworkBot, 'botId'>
  ): Promise<BotFrameworkBot> {
    const response = await this.apiFetch('/botframework', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bot),
    });
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`API error ${response.statusText}`);
    }
    const maybeBot = (await response.json()) as unknown;
    return BotFrameworkBotSchema.parse(maybeBot);
  }

  async putBot(bot: Immutable<BotFrameworkBot>): Promise<void> {
    // POST is due to strangeness with TDP service. 
    // Unsure if the semantics are PUT or PATCH, but the verb is POST
    // https://domoreexp.visualstudio.com/Teamspace/_git/teams-devapp?path=/AppStudio/AppStudio.API/Controllers/BotFrameworkController.cs&version=GBvNext&line=210&lineEnd=211&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
    const response = await this.apiFetch(`/botframework/${bot.botId}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bot),
    });
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`API error ${response.statusText}`);
    }
    //const maybeBot = (await response.json()) as unknown;
    //return BotFrameworkBotSchema.parse(maybeBot);
  }

  async getBots(): Promise<BotFrameworkBotResult> {
    const response = await this.apiFetch('/botframework');
    if (response.status < 200 || response.status >= 300) {
      return {
        type: 'networkError',
        errorCode: response.status,
      };
    }
    const body = (await response.json()) as unknown;
    const maybeBots = this.parseBots(body);
    if (!maybeBots.success) {
      this.logger.error('Invalid bots response %s', maybeBots.error.message);
      return {
        type: 'parseError',
        errors: maybeBots.error,
      };
    }
    this.logger.trace('Fetched %d bots', maybeBots.data.length);
    return {
      type: 'success',
      bots: maybeBots.data,
    };
  }

   async readBotDetails(botId: string): Promise<BotDetailsFetchResult> {
    try {
      this.logger.log('Fetching bot details for bot id: %s', botId);
      const response = await this.apiFetch(`/botframework/${botId}`);
      this.logger.log('Received response for bot details for bot id: %s | status: %d', botId, response.status);
      if (response.status !== 200) {
        this.logger.error(
          'Network error when fetching bot details for bot id: %s | reason: %s',
          botId,
          response.statusText
        );
        return {
          type: 'error',
          errorType: 'error',
          errorMessage: response.statusText,
        };
      }

    // TODO: Add Zod parsing schema
      const dto = (await response.json()) as unknown;
      const maybeSuccess = BotFrameworkBotSchema.safeParse(dto);
      if (!maybeSuccess.success) {
        this.logger.error(
          'Encountered parsing errors with payload from service for bot id: %s',
          botId
        );
        return { 
          type: 'error', 
          errorType: 'parsingError', 
          errorMessage: maybeSuccess.error.message 
        };
      }

      return { type: 'success', botDetails: maybeSuccess.data };
    } catch (error) {
      console.error('Error fetching bot details:', error);
      this.logger.error('Unexpected error fetching bot details for bot id: %s', botId);
      return {
        type: 'error',
        errorType: 'networkError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private parseBots(dto: unknown) {
    return z.array(BotFrameworkBotSchema).safeParse(dto);
  }
}
